import { trackEvent } from "@/utils/analytics";
import { trackErrorAnalysis } from "@/utils/analytics-dashboard";
import logger from "@/utils/logger";

/**
 * Error Categorization and Analysis Utility
 * Provides detailed error tracking and categorization for checkout flow optimization
 */

// Detailed error categories for better analysis
export const ERROR_CATEGORIES = {
  // Payment-related errors
  PAYMENT_CARD_DECLINED: "payment_card_declined",
  PAYMENT_INSUFFICIENT_FUNDS: "payment_insufficient_funds", 
  PAYMENT_EXPIRED_CARD: "payment_expired_card",
  PAYMENT_INVALID_CARD: "payment_invalid_card",
  PAYMENT_PROCESSING_ERROR: "payment_processing_error",
  PAYMENT_NETWORK_ERROR: "payment_network_error",
  PAYMENT_AUTHENTICATION_FAILED: "payment_authentication_failed",

  // Form validation errors
  VALIDATION_EMAIL_INVALID: "validation_email_invalid",
  VALIDATION_PHONE_INVALID: "validation_phone_invalid",
  VALIDATION_REQUIRED_FIELD: "validation_required_field",
  VALIDATION_FORMAT_ERROR: "validation_format_error",

  // Network and connectivity errors
  NETWORK_TIMEOUT: "network_timeout",
  NETWORK_CONNECTION_LOST: "network_connection_lost",
  NETWORK_SERVER_ERROR: "network_server_error",
  NETWORK_DNS_ERROR: "network_dns_error",

  // Account creation errors
  ACCOUNT_EMAIL_EXISTS: "account_email_exists",
  ACCOUNT_PHONE_EXISTS: "account_phone_exists",
  ACCOUNT_TWILIO_FAILED: "account_twilio_failed",
  ACCOUNT_DATABASE_ERROR: "account_database_error",
  ACCOUNT_EMAIL_SERVICE_FAILED: "account_email_service_failed",

  // Authentication errors
  AUTH_TOKEN_GENERATION_FAILED: "auth_token_generation_failed",
  AUTH_SESSION_CREATION_FAILED: "auth_session_creation_failed",
  AUTH_VERIFICATION_FAILED: "auth_verification_failed",

  // System errors
  SYSTEM_STRIPE_CONFIG_ERROR: "system_stripe_config_error",
  SYSTEM_API_UNAVAILABLE: "system_api_unavailable",
  SYSTEM_RATE_LIMIT_EXCEEDED: "system_rate_limit_exceeded",
  SYSTEM_MAINTENANCE_MODE: "system_maintenance_mode",

  // User behavior errors
  USER_ABANDONED_CHECKOUT: "user_abandoned_checkout",
  USER_MULTIPLE_TABS: "user_multiple_tabs",
  USER_SESSION_EXPIRED: "user_session_expired",
  USER_BROWSER_INCOMPATIBLE: "user_browser_incompatible",
};

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: "low",
  MEDIUM: "medium", 
  HIGH: "high",
  CRITICAL: "critical",
};

// Error recovery strategies
export const RECOVERY_STRATEGIES = {
  RETRY_AUTOMATIC: "retry_automatic",
  RETRY_MANUAL: "retry_manual",
  FALLBACK_METHOD: "fallback_method",
  CONTACT_SUPPORT: "contact_support",
  REDIRECT_ALTERNATIVE: "redirect_alternative",
  NO_RECOVERY: "no_recovery",
};

/**
 * Categorize error based on error message and context
 * @param {Error} error - Error object
 * @param {string} context - Context where error occurred
 * @returns {Object} Categorized error information
 */
export const categorizeError = (error, context) => {
  const errorMessage = error.message.toLowerCase();
  const errorCode = error.code || error.type || "unknown";

  let category = ERROR_CATEGORIES.SYSTEM_API_UNAVAILABLE; // Default
  let severity = ERROR_SEVERITY.MEDIUM; // Default
  let recoveryStrategy = RECOVERY_STRATEGIES.RETRY_MANUAL; // Default

  // Payment error categorization
  if (context.includes("payment") || context.includes("stripe")) {
    if (errorMessage.includes("card_declined") || errorMessage.includes("declined")) {
      category = ERROR_CATEGORIES.PAYMENT_CARD_DECLINED;
      severity = ERROR_SEVERITY.HIGH;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_MANUAL;
    } else if (errorMessage.includes("insufficient_funds")) {
      category = ERROR_CATEGORIES.PAYMENT_INSUFFICIENT_FUNDS;
      severity = ERROR_SEVERITY.HIGH;
      recoveryStrategy = RECOVERY_STRATEGIES.FALLBACK_METHOD;
    } else if (errorMessage.includes("expired")) {
      category = ERROR_CATEGORIES.PAYMENT_EXPIRED_CARD;
      severity = ERROR_SEVERITY.HIGH;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_MANUAL;
    } else if (errorMessage.includes("invalid") && errorMessage.includes("card")) {
      category = ERROR_CATEGORIES.PAYMENT_INVALID_CARD;
      severity = ERROR_SEVERITY.HIGH;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_MANUAL;
    } else if (errorMessage.includes("authentication")) {
      category = ERROR_CATEGORIES.PAYMENT_AUTHENTICATION_FAILED;
      severity = ERROR_SEVERITY.HIGH;
      recoveryStrategy = RECOVERY_STRATEGIES.CONTACT_SUPPORT;
    } else if (errorMessage.includes("network") || errorMessage.includes("timeout")) {
      category = ERROR_CATEGORIES.PAYMENT_NETWORK_ERROR;
      severity = ERROR_SEVERITY.MEDIUM;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_AUTOMATIC;
    } else {
      category = ERROR_CATEGORIES.PAYMENT_PROCESSING_ERROR;
      severity = ERROR_SEVERITY.HIGH;
      recoveryStrategy = RECOVERY_STRATEGIES.CONTACT_SUPPORT;
    }
  }

  // Validation error categorization
  else if (context.includes("validation") || context.includes("form")) {
    if (errorMessage.includes("email")) {
      category = ERROR_CATEGORIES.VALIDATION_EMAIL_INVALID;
      severity = ERROR_SEVERITY.LOW;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_MANUAL;
    } else if (errorMessage.includes("phone")) {
      category = ERROR_CATEGORIES.VALIDATION_PHONE_INVALID;
      severity = ERROR_SEVERITY.LOW;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_MANUAL;
    } else if (errorMessage.includes("required")) {
      category = ERROR_CATEGORIES.VALIDATION_REQUIRED_FIELD;
      severity = ERROR_SEVERITY.LOW;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_MANUAL;
    } else {
      category = ERROR_CATEGORIES.VALIDATION_FORMAT_ERROR;
      severity = ERROR_SEVERITY.LOW;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_MANUAL;
    }
  }

  // Network error categorization
  else if (context.includes("network") || errorMessage.includes("fetch") || errorMessage.includes("timeout")) {
    if (errorMessage.includes("timeout")) {
      category = ERROR_CATEGORIES.NETWORK_TIMEOUT;
      severity = ERROR_SEVERITY.MEDIUM;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_AUTOMATIC;
    } else if (errorMessage.includes("connection")) {
      category = ERROR_CATEGORIES.NETWORK_CONNECTION_LOST;
      severity = ERROR_SEVERITY.MEDIUM;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_AUTOMATIC;
    } else if (errorMessage.includes("server") || errorCode.startsWith("5")) {
      category = ERROR_CATEGORIES.NETWORK_SERVER_ERROR;
      severity = ERROR_SEVERITY.HIGH;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_AUTOMATIC;
    } else {
      category = ERROR_CATEGORIES.NETWORK_DNS_ERROR;
      severity = ERROR_SEVERITY.MEDIUM;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_AUTOMATIC;
    }
  }

  // Account creation error categorization
  else if (context.includes("account") || context.includes("registration")) {
    if (errorMessage.includes("email") && errorMessage.includes("exists")) {
      category = ERROR_CATEGORIES.ACCOUNT_EMAIL_EXISTS;
      severity = ERROR_SEVERITY.MEDIUM;
      recoveryStrategy = RECOVERY_STRATEGIES.REDIRECT_ALTERNATIVE;
    } else if (errorMessage.includes("phone") && errorMessage.includes("exists")) {
      category = ERROR_CATEGORIES.ACCOUNT_PHONE_EXISTS;
      severity = ERROR_SEVERITY.MEDIUM;
      recoveryStrategy = RECOVERY_STRATEGIES.REDIRECT_ALTERNATIVE;
    } else if (errorMessage.includes("twilio")) {
      category = ERROR_CATEGORIES.ACCOUNT_TWILIO_FAILED;
      severity = ERROR_SEVERITY.HIGH;
      recoveryStrategy = RECOVERY_STRATEGIES.FALLBACK_METHOD;
    } else if (errorMessage.includes("database")) {
      category = ERROR_CATEGORIES.ACCOUNT_DATABASE_ERROR;
      severity = ERROR_SEVERITY.CRITICAL;
      recoveryStrategy = RECOVERY_STRATEGIES.CONTACT_SUPPORT;
    } else if (errorMessage.includes("email") && errorMessage.includes("send")) {
      category = ERROR_CATEGORIES.ACCOUNT_EMAIL_SERVICE_FAILED;
      severity = ERROR_SEVERITY.MEDIUM;
      recoveryStrategy = RECOVERY_STRATEGIES.FALLBACK_METHOD;
    }
  }

  // Authentication error categorization
  else if (context.includes("auth") || context.includes("login")) {
    if (errorMessage.includes("token")) {
      category = ERROR_CATEGORIES.AUTH_TOKEN_GENERATION_FAILED;
      severity = ERROR_SEVERITY.HIGH;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_AUTOMATIC;
    } else if (errorMessage.includes("session")) {
      category = ERROR_CATEGORIES.AUTH_SESSION_CREATION_FAILED;
      severity = ERROR_SEVERITY.HIGH;
      recoveryStrategy = RECOVERY_STRATEGIES.RETRY_AUTOMATIC;
    } else {
      category = ERROR_CATEGORIES.AUTH_VERIFICATION_FAILED;
      severity = ERROR_SEVERITY.HIGH;
      recoveryStrategy = RECOVERY_STRATEGIES.CONTACT_SUPPORT;
    }
  }

  return {
    category,
    severity,
    recoveryStrategy,
    originalError: {
      message: error.message,
      code: errorCode,
      stack: error.stack ? error.stack.substring(0, 500) : null,
    },
    context,
    timestamp: new Date().toISOString(),
  };
};

/**
 * Track categorized error with detailed analysis
 * @param {Error} error - Original error object
 * @param {string} context - Context where error occurred
 * @param {Object} additionalData - Additional error context
 */
export const trackCategorizedError = (error, context, additionalData = {}) => {
  const categorizedError = categorizeError(error, context);
  
  const errorData = {
    ...categorizedError,
    user_agent: navigator.userAgent,
    url: window.location.href,
    referrer: document.referrer,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    connection_type: navigator.connection?.effectiveType || "unknown",
    ...additionalData,
  };

  // Track in analytics dashboard
  trackErrorAnalysis(categorizedError.category, {
    severity: categorizedError.severity,
    recovery_strategy: categorizedError.recoveryStrategy,
    context: context,
    error_message: error.message,
    ...additionalData,
  });

  // Track general error event
  trackEvent("categorized_error", errorData);

  logger.error("Categorized error tracked", errorData);

  return categorizedError;
};

/**
 * Get user-friendly error message based on error category
 * @param {string} errorCategory - Error category
 * @returns {Object} User-friendly error information
 */
export const getUserFriendlyErrorMessage = (errorCategory) => {
  const errorMessages = {
    [ERROR_CATEGORIES.PAYMENT_CARD_DECLINED]: {
      title: "Payment Declined",
      message: "Your card was declined. Please check your card details or try a different payment method.",
      action: "Try Again",
    },
    [ERROR_CATEGORIES.PAYMENT_INSUFFICIENT_FUNDS]: {
      title: "Insufficient Funds",
      message: "Your card has insufficient funds. Please use a different payment method.",
      action: "Use Different Card",
    },
    [ERROR_CATEGORIES.PAYMENT_EXPIRED_CARD]: {
      title: "Card Expired",
      message: "Your card has expired. Please update your card details.",
      action: "Update Card",
    },
    [ERROR_CATEGORIES.PAYMENT_INVALID_CARD]: {
      title: "Invalid Card",
      message: "The card information is invalid. Please check your card details.",
      action: "Check Details",
    },
    [ERROR_CATEGORIES.VALIDATION_EMAIL_INVALID]: {
      title: "Invalid Email",
      message: "Please enter a valid email address.",
      action: "Correct Email",
    },
    [ERROR_CATEGORIES.VALIDATION_PHONE_INVALID]: {
      title: "Invalid Phone Number",
      message: "Please enter a valid Australian phone number.",
      action: "Correct Phone",
    },
    [ERROR_CATEGORIES.NETWORK_TIMEOUT]: {
      title: "Connection Timeout",
      message: "The request timed out. Please check your internet connection and try again.",
      action: "Retry",
    },
    [ERROR_CATEGORIES.ACCOUNT_EMAIL_EXISTS]: {
      title: "Email Already Registered",
      message: "This email is already registered. Please log in or use a different email.",
      action: "Log In",
    },
    [ERROR_CATEGORIES.ACCOUNT_TWILIO_FAILED]: {
      title: "SMS Setup Failed",
      message: "We couldn't set up SMS authentication. You can still access your account via email.",
      action: "Continue",
    },
  };

  return errorMessages[errorCategory] || {
    title: "Something Went Wrong",
    message: "An unexpected error occurred. Please try again or contact support if the problem persists.",
    action: "Try Again",
  };
};

/**
 * Get recovery actions based on error category
 * @param {string} errorCategory - Error category
 * @returns {Array} Array of recovery actions
 */
export const getRecoveryActions = (errorCategory) => {
  const recoveryActions = {
    [ERROR_CATEGORIES.PAYMENT_CARD_DECLINED]: [
      { type: "retry", label: "Try Again", primary: true },
      { type: "different_card", label: "Use Different Card", primary: false },
      { type: "contact_bank", label: "Contact Your Bank", primary: false },
    ],
    [ERROR_CATEGORIES.PAYMENT_NETWORK_ERROR]: [
      { type: "retry", label: "Retry Payment", primary: true },
      { type: "check_connection", label: "Check Connection", primary: false },
    ],
    [ERROR_CATEGORIES.VALIDATION_EMAIL_INVALID]: [
      { type: "correct_email", label: "Correct Email", primary: true },
    ],
    [ERROR_CATEGORIES.ACCOUNT_EMAIL_EXISTS]: [
      { type: "login", label: "Log In Instead", primary: true },
      { type: "different_email", label: "Use Different Email", primary: false },
      { type: "reset_password", label: "Reset Password", primary: false },
    ],
    [ERROR_CATEGORIES.NETWORK_TIMEOUT]: [
      { type: "retry", label: "Try Again", primary: true },
      { type: "refresh", label: "Refresh Page", primary: false },
    ],
  };

  return recoveryActions[errorCategory] || [
    { type: "retry", label: "Try Again", primary: true },
    { type: "contact_support", label: "Contact Support", primary: false },
  ];
};

/**
 * Track error recovery attempt
 * @param {string} errorCategory - Original error category
 * @param {string} recoveryAction - Recovery action taken
 * @param {boolean} success - Whether recovery was successful
 * @param {Object} recoveryData - Additional recovery context
 */
export const trackErrorRecovery = (errorCategory, recoveryAction, success, recoveryData = {}) => {
  const eventData = {
    original_error_category: errorCategory,
    recovery_action: recoveryAction,
    recovery_success: success,
    recovery_time: recoveryData.recoveryTime,
    attempt_number: recoveryData.attemptNumber || 1,
    timestamp: new Date().toISOString(),
    ...recoveryData,
  };

  trackEvent("error_recovery_attempt", eventData);
  
  logger.log(`Error recovery tracked: ${recoveryAction} for ${errorCategory}`, eventData);

  return eventData;
};